import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";

// GET /v1/label-reserved-keys 강제 계약. 백엔드 entity/label/reserved.py 가 SoT 다.
// reserved key 는 environment/source/task 뿐이다. camera_count/sensor 는 reserved 가 아니라
// 하드웨어 축이고, embodiment 카탈로그가 처리한다. 프론트는 reserved 값을 하드코딩하지 않는다.
// 이 계약으로 입력 컨트롤을 렌더한다:
//   - allowlist: `values` 중 하나 (단일 Dropdown / ContentSwitcher / 다중선택)
//   - open: 자유 입력 (task). `values`는 비어 있다
// `default`는 입력 컨트롤을 미리 채울 권장 기본값이다. 없으면 null 이다(예: task).
export type ReservedKeyPolicy = "allowlist" | "open";

export type ReservedKeyContract = {
  key: string;
  policy: ReservedKeyPolicy;
  values: readonly string[];
  default: string | null;
  // 업로드 단계에서 선언을 강제하는 key 인지 나타낸다(backend UPLOAD_REQUIRED_LABEL_KEYS 미러).
  // 업로드 단계만 예약 라벨 선언을 요구한다. 확정 configuration 은 이 값을 보지 않는다.
  // 확정 configuration 에서 예약 라벨은 optional 이다.
  upload_required: boolean;
};

type ReservedKeysResp = {
  keys: ReservedKeyContract[];
};

// backend 가 정의하는 reserved key 전체 집합(entity/label/reserved.py 미러). environment/source/task.
// 이 목록은 FE 의 reserved key 값 SoT 다. 다른 모듈은 이 목록을 참조하고 문자열을 다시 적지
// 않는다. 허용값(allowlist)의 SoT 는 backend 계약(GET /v1/label-reserved-keys)이라 여기 두지 않는다.
export const RESERVED_KEYS: readonly string[] = ["environment", "source", "task"];

// 품질 라벨 key(backend `entity/label/reserved.py` 의 `QUALITY_KEY` 미러). reserved 는 아니다.
// 에피소드에만 붙는 key 다. 데이터셋에 부여하면 서버가 422 로 거절한다. 값 vocab 은
// `GET /v1/review-options` 의 `episode_quality_tags` 가 준다. 그래서 RESERVED_KEYS 옆에 두지만
// 섞지 않는다.
export const QUALITY_KEY = "quality";

// BE `validate_dataset_label_value` 가 Dataset 부여를 거절하는 에피소드 전용 축이다.
export const EPISODE_LABEL_KEYS: readonly string[] = [QUALITY_KEY, "intervention"];

// backend 필수 목록의 degraded-path 미러. 계약을 받지 못했을 때만 쓰는 최후 fallback 이다.
// 정상 경로에서는 계약의 `upload_required` 가 SoT 다. backend 를 쓸 수 없을 때 업로드 게이트가
// 전부 비지 않도록 environment/source 만 필수로 둔다. degraded 상태에서 task 는 필수가 아니다.
const UPLOAD_REQUIRED_FALLBACK_KEYS: readonly string[] = ["environment", "source"];

// 성공 응답에 없는 reserved key 를 open 정책 fallback 으로 채운다. 응답에 있는 key 는 그대로 둔다.
// 정상이면 backend 가 계약을 항상 준다. key 가 빠지면 New Label/리뷰 라벨 스텝에서 그 key 를
// 고를 수 없다(직접 입력만 가능). 빠진 key 만 채워 key 선택을 항상 보장한다.
export function mergeReservedFallback(
  keys: readonly ReservedKeyContract[],
): readonly ReservedKeyContract[] {
  const present = new Set(keys.map((contract) => contract.key));
  const missing = RESERVED_KEYS.filter((key) => !present.has(key)).map(
    (key): ReservedKeyContract => ({
      key,
      policy: "open",
      values: [],
      default: null,
      upload_required: UPLOAD_REQUIRED_FALLBACK_KEYS.includes(key),
    }),
  );
  return missing.length === 0 ? keys : [...keys, ...missing];
}

// static config (DB 무관)라 세션 동안 캐시한다.
let reservedKeyContractsCache: readonly ReservedKeyContract[] | null = null;
let reservedKeyContractsPromise: Promise<readonly ReservedKeyContract[]> | null = null;

export function getReservedKeyContracts(): Promise<readonly ReservedKeyContract[]> {
  if (reservedKeyContractsCache !== null) {
    return Promise.resolve(reservedKeyContractsCache);
  }
  if (reservedKeyContractsPromise !== null) {
    return reservedKeyContractsPromise;
  }

  const promise = apiClient
    .get<ReservedKeysResp>(apiRoutes.label.reservedKeys)
    .then((response) => {
      // 성공 응답은 빠진 reserved key 만 채운 뒤 세션 캐시에 저장한다.
      reservedKeyContractsCache = mergeReservedFallback(response.data.keys);
      return reservedKeyContractsCache;
    })
    .catch(() => {
      // 엔드포인트가 실패해도 New Label/리뷰 라벨 스텝에서 reserved key 선택이 사라지면 안 된다.
      // 그래서 open 정책 fallback 으로 응답한다. 이 응답은 캐시하지 않는다. 그래야 backend 가
      // 복구된 뒤 다음 호출이 실제 계약(allowlist)을 다시 받는다.
      return mergeReservedFallback([]);
    })
    .finally(() => {
      if (reservedKeyContractsPromise === promise) {
        reservedKeyContractsPromise = null;
      }
    });

  reservedKeyContractsPromise = promise;
  return promise;
}

// 동기 소비처(facet vocab 구성)용 allowlist 스냅샷. 계약을 아직 받지 못했거나 그 key 가
// allowlist 가 아니면 null 이다. 그러면 호출자가 자기 fallback vocab 을 쓴다.
// 세션 시작 때 prefetch 한다(`AppRoot` 의 labelInputWarmup 리소스). 그래서 실제로 쓸 때는
// 서버 계약 값이다.
export function reservedAllowlistSnapshot(key: string): readonly string[] | null {
  const contract = reservedKeyContractsCache?.find((entry) => entry.key === key);
  if (contract === undefined || contract.policy !== "allowlist" || contract.values.length === 0) {
    return null;
  }
  return contract.values;
}
